import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Metric, ReportLayoutType } from "@unbox/api";
import { handleError } from "@/api/error";
import { client, useScopedMerchantClient } from "./scoped-merchant-client";

export interface AnalyticsViewData {
  id: string;
  name: string;
  layout: ReportLayoutType;
  metrics: Metric[];
}

export interface LayoutItem {
  i: string;
  x: number;
  y: number;
  w: number;
  h: number;
}

// What the API returns
interface AnalyticsDashboardResponse {
  id: string;
  spec: {
    name: string;
    views: AnalyticsViewData[];
    layout: LayoutItem[];
  };
  merchantId: string;
  userId: string;
  createdAt: string;
  updatedAt: string;
}

// What the frontend uses (flattened)
export interface AnalyticsDashboard {
  id: string;
  name: string;
  views: AnalyticsViewData[];
  layout: LayoutItem[];
  merchantId: string;
  userId: string;
  createdAt: string;
  updatedAt: string;
}

export interface DashboardSpec {
  name: string;
  views: AnalyticsViewData[];
  layout: LayoutItem[];
}

export interface CreateDashboardInput {
  spec: DashboardSpec;
}

export interface UpdateDashboardInput {
  spec: DashboardSpec;
}

// Helper to flatten API response
const flattenDashboard = (response: AnalyticsDashboardResponse): AnalyticsDashboard => ({
  id: response.id,
  name: response.spec.name,
  views: response.spec.views,
  layout: response.spec.layout,
  merchantId: response.merchantId,
  userId: response.userId,
  createdAt: response.createdAt,
  updatedAt: response.updatedAt,
});

export const useAnalyticsDashboards = () => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: ["analytics-dashboards", scope.merchantId, scope.workspaceId],
    queryFn: async () => {
      if (!scope.merchantId) throw new Error("No merchant selected");

      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"]["analytics-dashboards"].$get({
            param: scope.param,
          })
        : await client.merchant[":merchantId"]["analytics-dashboards"].$get({
            param: scope.param,
          });

      if (!res.ok) throw await handleError(res);
      const response = (await res.json()) as AnalyticsDashboardResponse[];
      return response.map(flattenDashboard);
    },
    enabled: !!scope.merchantId,
  });
};

export const useAnalyticsDashboard = (id?: string) => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: ["analytics-dashboard", scope.merchantId, scope.workspaceId, id],
    queryFn: async () => {
      if (!scope.merchantId) throw new Error("No merchant selected");
      if (!id) throw new Error("No dashboard ID provided");

      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"]["analytics-dashboards"][":dashboardId"].$get({
            param: { ...scope.param, dashboardId: id },
          })
        : await client.merchant[":merchantId"]["analytics-dashboards"][":dashboardId"].$get({
            param: { ...scope.param, dashboardId: id },
          });

      if (!res.ok) throw await handleError(res);
      const response = (await res.json()) as AnalyticsDashboardResponse;
      return flattenDashboard(response);
    },
    enabled: !!scope.merchantId && !!id,
  });
};

export const useCreateAnalyticsDashboard = () => {
  const scope = useScopedMerchantClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateDashboardInput): Promise<{ id: string }> => {
      if (!scope.merchantId) throw new Error("No merchant selected");

      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"]["analytics-dashboards"].$post({
            param: scope.param,
            json: input,
          })
        : await client.merchant[":merchantId"]["analytics-dashboards"].$post({
            param: scope.param,
            json: input,
          });

      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["analytics-dashboards", scope.merchantId, scope.workspaceId] });
    },
  });
};

export const useUpdateAnalyticsDashboard = () => {
  const scope = useScopedMerchantClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: UpdateDashboardInput }) => {
      if (!scope.merchantId) throw new Error("No merchant selected");

      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"]["analytics-dashboards"][":dashboardId"].$put({
            param: { ...scope.param, dashboardId: id },
            json: input,
          })
        : await client.merchant[":merchantId"]["analytics-dashboards"][":dashboardId"].$put({
            param: { ...scope.param, dashboardId: id },
            json: input,
          });

      if (!res.ok) throw await handleError(res);
      const response = (await res.json()) as AnalyticsDashboardResponse;
      return flattenDashboard(response);
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ["analytics-dashboards", scope.merchantId, scope.workspaceId] });
      queryClient.invalidateQueries({ queryKey: ["analytics-dashboard", scope.merchantId, scope.workspaceId, id] });
    },
  });
};

export const useDeleteAnalyticsDashboard = () => {
  const scope = useScopedMerchantClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      if (!scope.merchantId) throw new Error("No merchant selected");

      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"]["analytics-dashboards"][
            ":dashboardId"
          ].$delete({
            param: { ...scope.param, dashboardId: id },
          })
        : await client.merchant[":merchantId"]["analytics-dashboards"][":dashboardId"].$delete({
            param: { ...scope.param, dashboardId: id },
          });

      if (!res.ok) throw await handleError(res);
    },
    onSuccess: (_, id) => {
      // Optimistically update the cache immediately
      queryClient.setQueryData(
        ["analytics-dashboards", scope.merchantId, scope.workspaceId],
        (oldData: AnalyticsDashboard[] | undefined) => {
          if (!oldData) return oldData;
          return oldData.filter((dashboard) => dashboard.id !== id);
        },
      );
      queryClient.removeQueries({ queryKey: ["analytics-dashboard", scope.merchantId, scope.workspaceId, id] });
      // Still invalidate to ensure consistency, but cache is already updated
      queryClient.invalidateQueries({ queryKey: ["analytics-dashboards", scope.merchantId, scope.workspaceId] });
    },
  });
};

export const useReorderAnalyticsDashboards = () => {
  const scope = useScopedMerchantClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (dashboardOrders: { id: string; order: number }[]): Promise<void> => {
      if (!scope.merchantId) throw new Error("No merchant selected");

      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"]["analytics-dashboards"].reorder.$post({
            param: scope.param,
            json: { dashboardOrders },
          })
        : await client.merchant[":merchantId"]["analytics-dashboards"].reorder.$post({
            param: scope.param,
            json: { dashboardOrders },
          });

      if (!res.ok) throw await handleError(res);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["analytics-dashboards", scope.merchantId, scope.workspaceId] });
    },
  });
};
